<template>
    <div>
        <ts-page-title
            title="Incentive Performance by CO"
            :breadcrumb="[
                { text: $t('home'), href: '/' },
                { text: 'Incentive Performance by CO', active: true }
            ]"
        />

        <!-- ────────────────── FILTER PANEL ────────────────── -->
        <ts-panel>
            <ts-panel-wrapper class="tw-py-3">
                <div
                    class="tw-flex tw-justify-between tw-items-center tw-cursor-pointer tw-select-none"
                    @click="filtersOpen = !filtersOpen"
                >
                    <div class="tw-flex tw-items-center tw-space-x-3 tw-flex-1 tw-min-w-0">
                        <i
                            class="fas tw-text-gray-400"
                            :class="filtersOpen ? 'fa-chevron-down' : 'fa-chevron-right'"
                            style="width: 12px"
                        ></i>
                        <b class="tw-text-gray-700 tw-whitespace-nowrap">Filter Criteria</b>
                        <div v-if="activeChips.length" class="tw-flex tw-flex-wrap tw-gap-1 tw-flex-1 tw-min-w-0">
                            <span
                                v-for="chip in activeChips"
                                :key="chip.key"
                                class="chip-pill"
                                :class="chip.tone || 'chip-default'"
                            >
                                <i v-if="chip.icon" :class="'fas ' + chip.icon + ' tw-mr-1'"></i>
                                {{ chip.label }}
                            </span>
                        </div>
                    </div>
                    <span class="tw-text-sm tw-text-blue-600 tw-whitespace-nowrap tw-ml-3">
                        {{ filtersOpen ? 'Collapse' : 'Edit filters' }}
                    </span>
                </div>

                <div v-show="filtersOpen" class="tw-mt-3">
                    <!-- Row 1: Cycle / Currency / Branch / Staff type -->
                    <div class="filter-grid">
                        <div class="filter-cell" style="grid-column: span 3">
                            <label class="filter-lbl">Cycle Month <span class="text-danger">*</span></label>
                            <DatePicker
                                type="month"
                                format="MM-yyyy"
                                placeholder="Pick a completed month"
                                style="width: 100%"
                                :value="model.cycle_month"
                                :options="datePickerOptions"
                                @on-change="onCycleChange"
                            ></DatePicker>
                        </div>

                        <div class="filter-cell" style="grid-column: span 2">
                            <label class="filter-lbl">Currency <span class="text-danger">*</span></label>
                            <div class="pill-group">
                                <button
                                    v-for="c in currencies"
                                    :key="c.currency_id"
                                    type="button"
                                    class="pill"
                                    :class="{ 'pill-active': model.currency_id === c.currency_id }"
                                    @click="model.currency_id = c.currency_id"
                                >{{ c.currency_code }}</button>
                            </div>
                        </div>

                        <div class="filter-cell" style="grid-column: span 3">
                            <label class="filter-lbl">Branch <span class="tw-text-gray-400 tw-font-normal tw-normal-case">(all if empty)</span></label>
                            <ts-branch-filter
                                @filter="value => (model.branch_id = value)"
                                :isPlaceholder="true"
                            />
                        </div>

                        <div class="filter-cell" style="grid-column: span 4">
                            <label class="filter-lbl">Staff Type</label>
                            <div class="pill-group">
                                <button
                                    type="button"
                                    class="pill"
                                    :class="{ 'pill-active': !model.staff_type }"
                                    @click="model.staff_type = null"
                                >All</button>
                                <button
                                    v-for="t in staffTypes"
                                    :key="t.id"
                                    type="button"
                                    class="pill"
                                    :class="{ 'pill-active': model.staff_type === t.id }"
                                    @click="model.staff_type = t.id"
                                >
                                    <span class="dot" :style="{ background: t.dot }"></span>
                                    {{ t.label }}
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Row 2: CO / Search + actions -->
                    <div class="filter-grid tw-mt-3 tw-items-end">
                        <div class="filter-cell" style="grid-column: span 4">
                            <label class="filter-lbl">Credit Officer</label>
                            <Select
                                v-model="model.co_id"
                                multiple
                                :max-tag-count="1"
                                :filterable="true"
                                :filter-by-label="true"
                                placeholder="All COs"
                            >
                                <Option
                                    v-for="item in employees"
                                    :value="item.employee_id"
                                    :key="item.employee_id"
                                    :label="item.employee_name_en"
                                >
                                    {{ item.employee_name_en }}
                                </Option>
                            </Select>
                        </div>

                        <div class="filter-cell" style="grid-column: span 8">
                            <label class="filter-lbl">Search</label>
                            <div class="search-group">
                                <div class="search-input-wrap">
                                    <input
                                        type="text"
                                        class="form-control search-input"
                                        v-model="searchQuery"
                                        placeholder="Card ID, CO name, SM card or SM name"
                                        @keyup.enter="onSearch"
                                    />
                                    <button
                                        v-if="searchQuery"
                                        type="button"
                                        class="search-clear"
                                        title="Clear search"
                                        @click.prevent="onClearSearch"
                                    ><i class="fas fa-times-circle"></i></button>
                                </div>
                                <button
                                    type="button"
                                    class="search-btn"
                                    :disabled="loading"
                                    @click.prevent="onSearch"
                                >
                                    <i class="fas fa-circle-notch fa-spin" v-if="loading"></i>
                                    <i class="fas fa-search" v-else></i>
                                    <span class="tw-ml-1">Search</span>
                                </button>
                                <button
                                    type="button"
                                    class="reset-btn"
                                    @click.prevent="onReset"
                                    title="Reset all filters"
                                ><i class="fas fa-undo"></i></button>
                            </div>
                        </div>
                    </div>
                </div>
            </ts-panel-wrapper>
        </ts-panel>

        <!-- ────────────────── SUMMARY CARDS ────────────────── -->
        <div class="summary-grid tw-mt-3">
            <div
                v-for="card in summaryCards"
                :key="card.key"
                class="summary-card"
                :class="'summary-card--' + card.tone"
            >
                <div class="summary-card__header">
                    <div class="summary-card__icon">
                        <i :class="'fas ' + card.icon"></i>
                    </div>
                    <div class="summary-card__label">{{ card.label }}</div>
                    <div v-if="card.badge !== undefined" class="summary-card__count">
                        <template v-if="summaryLoading">…</template>
                        <template v-else>{{ card.badge }}</template>
                    </div>
                </div>
                <div class="summary-card__value">
                    <template v-if="summaryLoading">
                        <span class="tw-text-gray-300">—</span>
                    </template>
                    <template v-else>
                        <span class="summary-card__amount">{{ card.formatted }}</span>
                        <span class="summary-card__currency">{{ card.unit }}</span>
                    </template>
                </div>
                <div
                    v-if="card.sub && !summaryLoading"
                    class="summary-card__sub"
                >
                    <span class="summary-card__sub-label">{{ card.sub.label }}</span>
                    <span class="summary-card__sub-value">{{ card.sub.value }}</span>
                </div>
            </div>
        </div>

        <!-- ────────────────── RESULTS CARD ────────────────── -->
        <div class="results-card tw-mt-3">
            <div class="results-toolbar">
                <div class="tw-flex tw-items-center tw-gap-3 tw-text-sm tw-flex-wrap">
                    <template v-if="loading">
                        <span class="tw-text-gray-500">
                            <i class="fas fa-circle-notch fa-spin tw-mr-1"></i> Loading…
                        </span>
                    </template>
                    <template v-else-if="meta.total === null">
                        <span class="tw-text-gray-400">
                            <i class="fas fa-info-circle tw-mr-1"></i> No search yet
                        </span>
                    </template>
                    <template v-else-if="meta.total === 0">
                        <span class="tw-text-gray-500">
                            <i class="far fa-folder-open tw-mr-1"></i> No results
                        </span>
                    </template>
                    <template v-else>
                        <span class="tw-font-bold tw-text-gray-800 tw-text-base">
                            {{ meta.total.toLocaleString() }} rows
                        </span>
                        <span class="tw-text-gray-500">
                            · Page {{ meta.current_page }} of {{ meta.last_page }}
                        </span>
                    </template>
                </div>

                <div class="export-group" title="Export the current filters to xlsx">
                    <select v-model="model.excel_template" class="export-select">
                        <option value="standard">Standard xlsx</option>
                        <option value="raw-data">Raw Data xlsx</option>
                    </select>
                    <button
                        type="button"
                        class="export-btn"
                        :disabled="exporting"
                        @click.prevent="exportExcel"
                    >
                        <i class="fas fa-circle-notch fa-spin" v-if="exporting"></i>
                        <i class="far fa-file-excel" v-else></i>
                        <span class="tw-ml-1">Export</span>
                    </button>
                </div>
            </div>

            <div
                class="results-body tw-relative"
                :style="{ minHeight: hasRows ? '0px' : emptyBodyHeight + 'px' }"
            >
                <Spin size="large" fix v-if="loading"></Spin>

                <div v-if="!loading && meta.total === null" class="empty-state">
                    <i class="fas fa-search tw-text-2xl tw-mb-2 tw-text-gray-300"></i>
                    <div class="tw-text-sm tw-text-gray-500">Ready when you are — click <b>Search</b> above.</div>
                </div>

                <div v-else-if="!loading && meta.total === 0" class="empty-state">
                    <i class="far fa-folder-open tw-text-2xl tw-mb-2 tw-text-gray-300"></i>
                    <div class="tw-text-sm tw-text-gray-500">No incentive rows for that month/branch.</div>
                    <div class="tw-text-xs tw-text-gray-400">Try a different cycle month or widen the branch scope.</div>
                </div>

                <Table
                    v-else
                    :columns="columns"
                    :data="groupedRows"
                    :row-class-name="rowClassName"
                    border
                    stripe
                    size="small"
                    :height="tableHeight"
                    show-summary
                    :summary-method="summaryMethod"
                    @on-sort-change="onSortChange"
                ></Table>
            </div>

            <div v-if="meta.total > 0" class="results-pagination">
                <div class="pagination-left">
                    <span class="pagination-lbl">Per page:</span>
                    <Select
                        v-model="perPage"
                        @on-change="onPerPageChange"
                        style="width: 80px"
                        size="small"
                    >
                        <Option :value="10">10</Option>
                        <Option :value="30">30</Option>
                        <Option :value="50">50</Option>
                        <Option :value="100">100</Option>
                    </Select>
                    <span class="pagination-divider">|</span>
                    <span class="pagination-lbl">
                        Showing <b>{{ rangeStart.toLocaleString() }}</b>–<b>{{ rangeEnd.toLocaleString() }}</b>
                        of <b>{{ meta.total.toLocaleString() }}</b>
                    </span>
                </div>
                <Page
                    :current="meta.current_page"
                    :total="meta.total || 0"
                    :page-size="perPage"
                    @on-change="onPageChange"
                    size="small"
                    show-elevator
                ></Page>
            </div>
        </div>
    </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapActions, mapState } from 'vuex'
import { debounce, trim } from 'lodash'
import moment from 'moment'
import * as FileDownload from 'downloadjs'

export default {
    name: 'incentivePerformanceByCoV3',
    data () {
        // Default to last month — the most recent completed cycle.
        const lastMonth = moment().subtract(1, 'month').format('MM-YYYY')
        return {
            filtersOpen: true,
            loading: false,
            exporting: false,
            errors: new Errors(),
            searchQuery: '',
            perPage: 30,
            viewportHeight: window.innerHeight,
            summaryLoading: false,
            summary: {
                staff:       { count: 0, so: 0, sm: 0 },
                active:      { total: 0 },
                incentive:   { total: '0' },
                outstanding: { total: '0' }
            },
            totals: {
                count: 0,
                total_active: 0,
                overdue_10_day: 0,
                overdue_10_day_with_xx_amount: 0,
                incentive_amount:    '0',
                outstanding_balance: '0'
            },
            rows: [],
            meta: { current_page: 1, last_page: 1, total: null },
            staffTypes: [
                { id: 'SO', label: 'SO', dot: '#3b82f6' },
                { id: 'SM', label: 'SM', dot: '#8b5cf6' }
            ],
            model: {
                branch_id: [],
                co_id: [],
                currency_id: null,
                cycle_month: lastMonth,
                staff_type: null,
                search: null,
                sort: 'branch_code',
                sort_dir: 'asc',
                page: 1,
                perPage: 30,
                excel_template: 'standard'
            }
        }
    },
    created () {
        this.model.currency_id = this.$store.state.authUser.sys_setting[0].default_currency_id
    },
    computed: {
        ...mapState('report/resource', ['employees', 'currencies']),

        currencyCode () {
            if (!this.model.currency_id) return ''
            const c = (this.currencies || []).find(c => c.currency_id === this.model.currency_id)
            return c ? c.currency_code : ''
        },
        isKh () {
            const locale = (this.$i18n && this.$i18n.locale) || localStorage.getItem('langCode')
            return locale === 'kh'
        },

        datePickerOptions () {
            return {
                // Only allow months < current month.
                disabledDate (date) {
                    const startOfThisMonth = moment().startOf('month').toDate()
                    return date && date.getTime() >= startOfThisMonth.getTime()
                }
            }
        },

        activeChips () {
            const chips = []
            if (this.model.cycle_month) {
                const m = moment(this.model.cycle_month, 'MM-YYYY')
                chips.push({ key: 'cycle', icon: 'fa-calendar-alt', label: m.format('MMM YYYY'), tone: 'chip-date' })
            }
            if (this.model.branch_id && this.model.branch_id.length) {
                chips.push({ key: 'branch', icon: 'fa-building',
                    label: this.model.branch_id.length + ' branch' + (this.model.branch_id.length > 1 ? 'es' : ''),
                    tone: 'chip-branch' })
            } else {
                chips.push({ key: 'branch', icon: 'fa-building', label: 'All branches', tone: 'chip-branch' })
            }
            if (this.model.currency_id) {
                const cur = this.currencies.find(c => c.currency_id === this.model.currency_id)
                if (cur) chips.push({ key: 'currency', label: cur.currency_code, tone: 'chip-currency' })
            }
            if (this.model.staff_type) {
                chips.push({ key: 'type', icon: 'fa-users', label: this.model.staff_type, tone: 'chip-type' })
            }
            if (this.model.co_id && this.model.co_id.length) {
                chips.push({ key: 'co', icon: 'fa-user',
                    label: this.model.co_id.length + ' CO' + (this.model.co_id.length > 1 ? 's' : ''),
                    tone: 'chip-co' })
            }
            if (this.searchQuery && this.searchQuery.trim().length >= 2) {
                chips.push({ key: 'search', icon: 'fa-search', label: '"' + this.searchQuery.trim() + '"', tone: 'chip-search' })
            }
            return chips.filter(c => c.label)
        },

        tableHeight () {
            const chrome = this.filtersOpen ? 601 : 471
            const h = this.viewportHeight - chrome
            return Math.max(280, h)
        },
        emptyBodyHeight () {
            return this.tableHeight + 52
        },
        hasRows () {
            return !this.loading && this.meta.total > 0
        },
        rangeStart () {
            if (!this.meta.total) return 0
            return (this.meta.current_page - 1) * this.perPage + 1
        },
        rangeEnd () {
            if (!this.meta.total) return 0
            return Math.min(this.meta.current_page * this.perPage, this.meta.total)
        },

        summaryCards () {
            const s = this.summary
            return [
                {
                    key: 'staff', tone: 'staff', icon: 'fa-users',
                    label: 'Staff Rows',
                    badge: (s.staff.count || 0).toLocaleString(),
                    formatted: (s.staff.so || 0).toLocaleString(),
                    unit: 'SO',
                    sub: { label: 'sm', value: (s.staff.sm || 0).toLocaleString() }
                },
                {
                    key: 'active', tone: 'active', icon: 'fa-user-check',
                    label: 'Total Active Accounts',
                    formatted: (s.active.total || 0).toLocaleString(),
                    unit: 'accounts'
                },
                {
                    key: 'incentive', tone: 'incentive', icon: 'fa-hand-holding-usd',
                    label: 'Total Incentive Amount',
                    formatted: this.fmt(s.incentive.total),
                    unit: this.currencyCode
                },
                {
                    key: 'outstanding', tone: 'outstanding', icon: 'fa-wallet',
                    label: 'Outstanding Balance',
                    formatted: this.fmt(s.outstanding.total),
                    unit: this.currencyCode
                }
            ]
        },

        /**
         * Injects a divider row before each staff_type group AND a subtotal
         * row at the end of each group — mirrors the v2 Excel layout
         * ("Staff Type: SM (5 detail record)" header + "Total Amounts by
         * staff type" red row). Groups are ordered SM → SO.
         */
        groupedRows () {
            if (!this.rows || !this.rows.length) return []

            const buckets = {}
            for (const r of this.rows) {
                const key = r.staff_type || 'Other'
                if (!buckets[key]) buckets[key] = []
                buckets[key].push(r)
            }

            const order = ['SM', 'SO']
            const keys = Object.keys(buckets).sort((a, b) => {
                const ia = order.indexOf(a), ib = order.indexOf(b)
                if (ia === -1 && ib === -1) return a.localeCompare(b)
                if (ia === -1) return 1
                if (ib === -1) return -1
                return ia - ib
            })

            const sumNum = (arr, k) => arr.reduce((s, r) => s + (parseFloat(r[k]) || 0), 0)

            const out = []
            for (const key of keys) {
                const items = buckets[key]
                out.push({
                    _divider: true,
                    staff_type: key,
                    _dividerLabel: 'Staff Type: ' + key + ' (' + items.length + ' detail record' + (items.length === 1 ? '' : 's') + ')'
                })
                for (const r of items) out.push(r)
                out.push({
                    _subtotal: true,
                    staff_type: key,
                    _subtotalLabel: 'Total ' + key + ' (' + items.length + ')',
                    total_active:                  sumNum(items, 'total_active'),
                    outstanding_balance:           sumNum(items, 'outstanding_balance'),
                    overdue_10_day:                sumNum(items, 'overdue_10_day'),
                    overdue_10_day_with_xx_amount: sumNum(items, 'overdue_10_day_with_xx_amount'),
                    active_client:                 sumNum(items, 'active_client'),
                    incentive_amount:              sumNum(items, 'incentive_amount')
                })
            }
            return out
        },

        columns () {
            // Column set mirrors the v2 Excel:
            //   Branch | SM Card# | SM Name | SO Card # | CO Name | Finish Probation |
            //   Active Client Total | Outstanding Balance | # Clients Late >= 10Days |
            //   Late >=, Paid >= 200,000 | Active Clients | Amt Inc Paid
            // (Type chip stays on the far left for scannability, though v2 groups by it.)
            return [
                {
                    title: 'Branch', key: 'branch_code', width: 110, fixed: 'left', align: 'center', sortable: 'custom',
                    render: (h, params) => {
                        if (params.row._divider) {
                            return h('span', { class: 'group-header-label' }, params.row._dividerLabel)
                        }
                        if (params.row._subtotal) {
                            return h('span', { class: 'group-subtotal-label' }, params.row._subtotalLabel)
                        }
                        return h('span', { class: 'branch-code' }, params.row.branch_code || '—')
                    }
                },
                {
                    title: 'SM Card#', key: 'sm_card_id', width: 100, align: 'center',
                    render: (h, params) => {
                        if (params.row._divider || params.row._subtotal) return h('span')
                        return h('code', { class: 'tw-text-xs tw-text-gray-700' }, params.row.sm_card_id || '—')
                    }
                },
                {
                    title: 'SM Name', key: 'sm_name_en', minWidth: 180,
                    render: (h, params) => {
                        if (params.row._divider || params.row._subtotal) return h('span')
                        return h('span', { class: 'name-text' }, this.isKh
                            ? (params.row.sm_name_kh || params.row.sm_name_en || '—')
                            : (params.row.sm_name_en || params.row.sm_name_kh || '—'))
                    }
                },
                {
                    title: 'SO Card #', key: 'card_id', width: 100, align: 'center', sortable: 'custom',
                    render: (h, params) => {
                        if (params.row._divider || params.row._subtotal) return h('span')
                        return h('code', { class: 'tw-text-xs tw-text-gray-700' }, params.row.card_id || '—')
                    }
                },
                {
                    title: 'CO Name', key: 'co_name_en', minWidth: 200, sortable: 'custom',
                    render: (h, params) => {
                        if (params.row._divider || params.row._subtotal) return h('span')
                        return h('span', { class: 'name-text' }, this.isKh
                            ? (params.row.co_name_kh || params.row.co_name_en || '—')
                            : (params.row.co_name_en || params.row.co_name_kh || '—'))
                    }
                },
                {
                    title: 'Finish Probation', key: 'is_finish_probation', width: 130, align: 'center',
                    render: (h, params) => {
                        if (params.row._divider || params.row._subtotal) return h('span')
                        const finished = !!params.row.is_finish_probation
                        return h('span', {
                            class: 'probation-pill probation-pill--' + (finished ? 'yes' : 'no')
                        }, finished ? 'Yes' : 'No')
                    }
                },
                {
                    title: 'Active Client Total', key: 'total_active', width: 140, align: 'right', sortable: 'custom',
                    render: (h, params) => {
                        if (params.row._divider) return h('span')
                        return h('span', { class: 'tw-font-semibold tw-text-gray-800' },
                            (params.row.total_active || 0).toLocaleString())
                    }
                },
                {
                    title: 'Outstanding Balance', key: 'outstanding_balance', width: 150, align: 'right', sortable: 'custom',
                    render: (h, params) => {
                        if (params.row._divider) return h('span')
                        const n = parseFloat(params.row.outstanding_balance)
                        if (isNaN(n) || n === 0) return h('span', { class: 'tw-text-gray-300' }, '—')
                        return h('span', this.fmt(params.row.outstanding_balance))
                    }
                },
                {
                    title: '# Clients Late ≥ 10Days', key: 'overdue_10_day', width: 165, align: 'right', sortable: 'custom',
                    render: (h, params) => {
                        if (params.row._divider) return h('span')
                        return h('span', (params.row.overdue_10_day || 0).toLocaleString())
                    }
                },
                {
                    title: 'Late ≥, Paid ≥ 200,000', key: 'overdue_10_day_with_xx_amount', width: 165, align: 'right',
                    render: (h, params) => {
                        if (params.row._divider) return h('span')
                        return h('span', (params.row.overdue_10_day_with_xx_amount || 0).toLocaleString())
                    }
                },
                {
                    title: 'Active Clients', key: 'active_client', width: 115, align: 'right', sortable: 'custom',
                    render: (h, params) => {
                        if (params.row._divider) return h('span')
                        return h('span', (parseFloat(params.row.active_client) || 0).toLocaleString())
                    }
                },
                {
                    title: 'Amt Inc Paid', key: 'incentive_amount', minWidth: 130, align: 'right', sortable: 'custom',
                    render: (h, params) => {
                        if (params.row._divider) return h('span')
                        const n = parseFloat(params.row.incentive_amount)
                        if (isNaN(n) || n === 0) return h('span', { class: 'tw-text-gray-300' }, '—')
                        return h('span', {
                            class: params.row._subtotal
                                ? 'tw-font-semibold'
                                : 'tw-font-semibold tw-text-green-700'
                        }, this.fmt(params.row.incentive_amount))
                    }
                }
            ]
        }
    },
    methods: {
        ...mapActions('report/resource', ['getEmployee', 'getCurrency']),

        fmt (v) {
            const n = parseFloat(v)
            if (isNaN(n) || n === 0) return '—'
            return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
        },
        fmtStrict (v) {
            const n = parseFloat(v)
            if (isNaN(n)) return '0'
            return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
        },

        rowClassName (row) {
            if (!row) return ''
            if (row._divider)  return 'group-header-row'
            if (row._subtotal) return 'group-subtotal-row'
            return ''
        },

        summaryMethod ({ columns }) {
            const t = this.totals
            const sums = {}

            columns.forEach((col, idx) => {
                if (idx === 0) {
                    sums[col.key] = { key: col.key, value: 'TOTAL' }
                    return
                }
                let value = ''
                switch (col.key) {
                    case 'total_active':
                        value = (t.total_active || 0).toLocaleString(); break
                    case 'outstanding_balance':
                        value = this.fmtStrict(t.outstanding_balance); break
                    case 'overdue_10_day':
                        value = (t.overdue_10_day || 0).toLocaleString(); break
                    case 'overdue_10_day_with_xx_amount':
                        value = (t.overdue_10_day_with_xx_amount || 0).toLocaleString(); break
                    case 'incentive_amount':
                        value = this.fmtStrict(t.incentive_amount); break
                    default:
                        value = ''
                }
                sums[col.key] = { key: col.key, value }
            })
            return sums
        },

        onCycleChange (val) {
            this.model.cycle_month = val || null
        },

        buildParams () {
            const p = {
                branch_id:      this.model.branch_id,
                co_id:          this.model.co_id,
                currency_id:    this.model.currency_id,
                cycle_month:    this.model.cycle_month,
                staff_type:     this.model.staff_type,
                sort:           this.model.sort,
                sort_dir:       this.model.sort_dir,
                page:           this.model.page,
                perPage:        this.perPage,
                excel_template: this.model.excel_template
            }
            if (this.searchQuery && this.searchQuery.trim().length >= 2) {
                p.search = this.searchQuery.trim()
            }
            return p
        },

        fetchData () {
            this.errors = new Errors()
            this.loading = true
            this.summaryLoading = true

            this.$store
                .dispatch('report/creditOperation/incentivePerformanceByCoV3', this.buildParams())
                .then(response => {
                    const data    = response.data || []
                    const meta    = response.meta || response
                    const summary = response.summary || null
                    const totals  = response.totals  || null

                    this.rows = data
                    this.meta = {
                        current_page: meta.current_page || 1,
                        last_page:    meta.last_page    || 1,
                        total:        meta.total ?? 0
                    }

                    if (summary) {
                        this.summary = {
                            staff:       Object.assign({ count: 0, so: 0, sm: 0 }, summary.staff       || {}),
                            active:      Object.assign({ total: 0 },               summary.active      || {}),
                            incentive:   Object.assign({ total: '0' },             summary.incentive   || {}),
                            outstanding: Object.assign({ total: '0' },             summary.outstanding || {})
                        }
                    }
                    if (totals) {
                        this.totals = Object.assign(
                            { count: 0, total_active: 0, overdue_10_day: 0,
                              overdue_10_day_with_xx_amount: 0,
                              incentive_amount: '0', outstanding_balance: '0' },
                            totals
                        )
                    }
                })
                .catch(error => {
                    this.notice({ type: 'error', text: (error && error.message) || 'Failed to load report' })
                    this.errors = new Errors((error && error.errors) || {})
                    this.rows = []
                })
                .finally(() => {
                    this.loading = false
                    this.summaryLoading = false
                })
        },

        onSearch () {
            if (!this.model.cycle_month) {
                this.notice({ type: 'warning', text: 'Please pick a cycle month first.' })
                return
            }
            this.model.page = 1
            this.filtersOpen = false
            this.fetchData()
        },

        onClearSearch () {
            const hadSearch = !!(this.searchQuery && this.searchQuery.trim())
            this.searchQuery = ''
            if (hadSearch && this.meta.total !== null) {
                this.model.page = 1
                this.fetchData()
            }
        },

        onReset () {
            this.searchQuery = ''
            this.model.co_id = []
            this.model.staff_type = null
            this.model.cycle_month = moment().subtract(1, 'month').format('MM-YYYY')
            this.model.sort = 'branch_code'
            this.model.sort_dir = 'asc'
            this.model.page = 1
            this.rows = []
            this.meta = { current_page: 1, last_page: 1, total: null }
        },

        onSortChange ({ key, order }) {
            if (order === 'normal') {
                this.model.sort = 'branch_code'
                this.model.sort_dir = 'asc'
            } else {
                this.model.sort = key
                this.model.sort_dir = order === 'asc' ? 'asc' : 'desc'
            }
            this.model.page = 1
            this.fetchData()
        },

        onPageChange (page) {
            this.model.page = page
            this.fetchData()
        },
        onPerPageChange (v) {
            this.perPage = v
            this.model.page = 1
            this.fetchData()
        },

        exportExcel () {
            this.errors = new Errors()
            this.exporting = true

            this.$store
                .dispatch('report/creditOperation/incentivePerformanceByCoV3ExportExcel', this.buildParams())
                .then(response => {
                    const filename = trim(
                        response.headers['content-disposition']
                            .substring(response.headers['content-disposition'].indexOf('filename'))
                            .replace('filename=', ''),
                        '"'
                    )
                    FileDownload(response.data, filename)
                })
                .catch(err => {
                    let msg = 'Export failed'
                    try {
                        const parsed = JSON.parse(String.fromCharCode.apply(null, new Uint8Array(err)))
                        msg = parsed.message || msg
                        this.errors = new Errors(parsed.errors || {})
                    } catch (e) { /* not JSON */ }
                    this.notice({ type: 'error', text: msg })
                })
                .finally(() => { this.exporting = false })
        },

        notice (not) {
            this.$Notice[not.type]({
                title: 'INCENTIVE PERFORMANCE BY CO',
                desc: not.text
            })
        }
    },
    watch: {
        'model.branch_id': debounce(function (value) {
            this.model.co_id = []
            this.getEmployee({ branch_id: value })
        }, 500),
        'model.staff_type' () {
            if (this.meta.total !== null) {
                this.model.page = 1
                this.fetchData()
            }
        }
    },
    mounted () {
        this._onResize = debounce(() => { this.viewportHeight = window.innerHeight }, 100)
        window.addEventListener('resize', this._onResize)
    },
    beforeDestroy () {
        if (this._onResize) window.removeEventListener('resize', this._onResize)
    },
    beforeRouteEnter (to, from, next) {
        next(vm => { vm.getCurrency() })
    }
}
</script>

<style scoped>
.filter-grid {
    display: grid;
    grid-template-columns: repeat(12, minmax(0, 1fr));
    gap: 12px;
    align-items: end;
}
.filter-cell { min-width: 0; }
.filter-lbl {
    display: block; font-size: 11px; font-weight: 600;
    text-transform: uppercase; letter-spacing: 0.03em;
    color: #6b7280; margin-bottom: 4px; height: 15px;
}
::v-deep .ivu-select-selection { min-height: 34px; }
::v-deep .ivu-input { height: 34px; }

.pill-group {
    display: inline-flex; align-items: center; flex-wrap: nowrap; gap: 3px;
    padding: 3px; background: #f3f4f6; border-radius: 6px;
    height: 34px; box-sizing: border-box; overflow-x: auto; max-width: 100%;
}
.pill {
    background: transparent; border: none; padding: 4px 10px;
    font-size: 12px; color: #4b5563; border-radius: 4px;
    cursor: pointer; transition: all 0.15s; line-height: 1.2;
    white-space: nowrap; display: inline-flex; align-items: center;
}
.pill:hover { background: #e5e7eb; }
.pill-active {
    background: #ffffff; color: #111827; font-weight: 600;
    box-shadow: 0 1px 2px rgba(0,0,0,0.08);
}
.pill .dot {
    display: inline-block; width: 6px; height: 6px;
    border-radius: 50%; margin-right: 5px;
}

.chip-pill {
    display: inline-flex; align-items: center; padding: 2px 8px;
    font-size: 11px; border-radius: 10px;
    background: #f3f4f6; color: #374151; line-height: 1.5; white-space: nowrap;
}
.chip-default  { background: #f3f4f6; color: #374151; }
.chip-branch   { background: #eef2ff; color: #4338ca; }
.chip-currency { background: #ecfdf5; color: #047857; }
.chip-date     { background: #fef3c7; color: #92400e; }
.chip-type     { background: #f5f3ff; color: #6b21a8; }
.chip-co       { background: #f3e8ff; color: #6b21a8; }
.chip-search   { background: #fce7f3; color: #9d174d; }

@media (max-width: 991px) {
    .filter-grid > .filter-cell { grid-column: span 12 !important; }
}

.search-group {
    display: flex; align-items: stretch; height: 34px;
    border: 1px solid #d1d5db; border-radius: 6px;
    overflow: hidden; background: #ffffff;
}
.search-input-wrap { position: relative; flex: 1; display: flex; align-items: stretch; min-width: 0; }
.search-input {
    flex: 1; border: none !important; height: 100% !important;
    padding: 0 32px 0 12px; outline: none; background: transparent;
    font-size: 13px; color: #111827; min-width: 0;
}
.search-input:focus { box-shadow: none !important; }
.search-clear {
    position: absolute; right: 6px; top: 50%; transform: translateY(-50%);
    background: transparent; border: none; padding: 4px;
    font-size: 14px; color: #9ca3af; cursor: pointer;
}
.search-btn {
    background: #3b82f6; color: white; border: none;
    padding: 0 16px; font-size: 13px; font-weight: 600;
    cursor: pointer; display: inline-flex; align-items: center;
}
.search-btn:hover:not(:disabled) { background: #2563eb; }
.search-btn:disabled { background: #93c5fd; cursor: wait; }
.reset-btn {
    background: #f9fafb; color: #6b7280; border: none;
    border-left: 1px solid #e5e7eb; padding: 0 12px; cursor: pointer;
}
.reset-btn:hover { background: #f3f4f6; color: #374151; }

.results-card {
    background: #ffffff; border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05); overflow: hidden;
}
.results-toolbar {
    display: flex; justify-content: space-between; align-items: center;
    padding: 12px 16px; border-bottom: 1px solid #e5e7eb;
    background: #ffffff; gap: 8px; flex-wrap: wrap;
}
.results-body { min-height: 120px; }
.results-pagination {
    display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center;
    padding: 10px 16px; border-top: 1px solid #e5e7eb;
    background: #f9fafb; gap: 8px; min-height: 52px;
}
.pagination-left {
    display: flex; align-items: center; gap: 10px;
    height: 28px; line-height: 28px; font-size: 13px; color: #6b7280;
}
.pagination-lbl { display: inline-flex; align-items: center; height: 28px; line-height: 28px; white-space: nowrap; }
.pagination-lbl b { color: #111827; font-weight: 600; margin: 0 2px; }
.pagination-divider { color: #d1d5db; line-height: 28px; }

.summary-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
}
@media (max-width: 991px) { .summary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 575px) { .summary-grid { grid-template-columns: 1fr; } }
.summary-card {
    display: flex; flex-direction: column; justify-content: space-between; gap: 8px;
    padding: 12px 14px; background: #ffffff;
    border: 1px solid #e5e7eb; border-top: 3px solid;
    border-radius: 6px; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    min-height: 110px;
}
.summary-card__header { display: flex; align-items: center; gap: 8px; }
.summary-card__icon {
    width: 22px; height: 22px; display: flex; align-items: center; justify-content: center;
    border-radius: 4px; font-size: 11px; flex-shrink: 0;
}
.summary-card__label {
    flex: 1; font-size: 11px; font-weight: 600; text-transform: uppercase;
    letter-spacing: 0.03em; color: #6b7280; line-height: 1.2;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.summary-card__count {
    font-size: 11px; font-weight: 600; color: #9ca3af;
    padding: 1px 6px; background: #f3f4f6; border-radius: 10px; line-height: 1.4;
}
.summary-card__value {
    display: flex; align-items: baseline; gap: 6px;
    line-height: 1; white-space: nowrap; overflow: hidden;
}
.summary-card__amount {
    font-size: 20px; font-weight: 700; color: #111827;
    overflow: hidden; text-overflow: ellipsis; min-width: 0;
}
.summary-card__currency {
    font-size: 11px; font-weight: 600; color: #9ca3af;
    letter-spacing: 0.03em; flex-shrink: 0;
}
.summary-card__sub {
    display: flex; align-items: baseline; gap: 4px;
    padding-top: 6px; border-top: 1px dashed #e5e7eb;
    font-size: 11px; line-height: 1.2;
    white-space: nowrap; overflow: hidden;
}
.summary-card__sub-label {
    color: #9ca3af; text-transform: uppercase; letter-spacing: 0.03em;
    font-weight: 600; font-size: 10px;
}
.summary-card__sub-value {
    color: #374151; font-weight: 700;
    overflow: hidden; text-overflow: ellipsis; min-width: 0; flex: 1;
}
.summary-card--staff       { border-top-color: #6366f1; }
.summary-card--staff       .summary-card__icon { background: #eef2ff; color: #4338ca; }
.summary-card--active      { border-top-color: #3b82f6; }
.summary-card--active      .summary-card__icon { background: #eff6ff; color: #1e40af; }
.summary-card--incentive   { border-top-color: #10b981; }
.summary-card--incentive   .summary-card__icon { background: #ecfdf5; color: #047857; }
.summary-card--outstanding { border-top-color: #f59e0b; }
.summary-card--outstanding .summary-card__icon { background: #fef3c7; color: #92400e; }

::v-deep .ivu-table th {
    background: #f9fafb !important;
    height: 40px; color: #374151; font-weight: 600;
    font-size: 12px; text-transform: uppercase; letter-spacing: 0.02em;
}
::v-deep .ivu-table th .ivu-table-cell {
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
::v-deep .ivu-table-summary td {
    background: #f9fafb !important; font-weight: 700 !important;
    color: #111827 !important; border-top: 2px solid #d1d5db !important;
}
::v-deep tr.group-header-row td {
    background: #eef2ff !important;
    color: #4338ca !important;
    font-weight: 600 !important;
    border-top: 2px solid #c7d2fe !important;
    border-bottom: 1px solid #c7d2fe !important;
    height: 30px !important;
}
::v-deep tr.group-header-row td:not(:first-child) .ivu-table-cell > * {
    visibility: hidden;
}
::v-deep tr.group-header-row td:first-child .ivu-table-cell {
    overflow: visible !important;
    white-space: nowrap;
    text-align: left !important;
    padding-left: 12px !important;
}
::v-deep tr.group-subtotal-row td {
    background: #fef2f2 !important;
    color: #b91c1c !important;
    font-weight: 700 !important;
    border-top: 1px solid #fecaca !important;
    border-bottom: 1px solid #fecaca !important;
}
::v-deep tr.group-subtotal-row td:first-child .ivu-table-cell {
    overflow: visible !important;
    white-space: nowrap;
    text-align: left !important;
    padding-left: 12px !important;
}
::v-deep .ivu-table td .ivu-table-cell {
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis; padding: 4px 12px;
}
::v-deep .ivu-table td { height: 34px; }
::v-deep .ivu-table-small td { height: 32px; }

.export-group {
    display: inline-flex; align-items: stretch; height: 32px;
    border-radius: 5px; overflow: hidden;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    border: 1px solid #d1d5db; background: #ffffff;
}
.export-select {
    border: 0; border-right: 1px solid #d1d5db;
    background: #ffffff; padding: 0 28px 0 12px;
    font-size: 12px; color: #374151; font-weight: 500;
    cursor: pointer; outline: none; height: 100%;
    appearance: none; -webkit-appearance: none;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3e%3cpath fill='%236b7280' d='M5 6L0 0h10z'/%3e%3c/svg%3e");
    background-repeat: no-repeat; background-position: right 10px center; background-size: 10px 6px;
}
.export-btn {
    background: #10b981; color: white; border: none;
    padding: 0 16px; font-size: 13px; font-weight: 600;
    cursor: pointer; display: inline-flex; align-items: center;
}
.export-btn:hover:not(:disabled) { background: #059669; }
.export-btn:disabled { background: #e5e7eb; color: #9ca3af; cursor: not-allowed; }

.empty-state {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    padding: 40px 20px; text-align: center; height: 100%; position: absolute; inset: 0;
}
</style>

<!-- Unscoped: render-function elements don't receive data-v-xxx. -->
<style>
.name-text {
    color: #111827; font-size: 12px; font-weight: 500;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.branch-code {
    display: inline-block; padding: 1px 6px;
    font-size: 11px; font-weight: 600;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    color: #4338ca; background: #eef2ff;
    border: 1px solid #e0e7ff; border-radius: 3px;
    letter-spacing: 0.02em;
}
.type-chip {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 0 8px; height: 20px;
    font-size: 11px; font-weight: 600;
    border-radius: 10px; border: 1px solid;
    letter-spacing: 0.02em; white-space: nowrap;
}
.type-dot {
    display: inline-block; width: 6px; height: 6px;
    border-radius: 50%; flex-shrink: 0;
}
.name-cell {
    display: flex; flex-direction: column; gap: 2px;
    overflow: hidden;
}
.sm-hint {
    font-size: 10px;
    color: #9ca3af;
    letter-spacing: 0.02em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.probation-pill {
    display: inline-block;
    padding: 1px 8px;
    font-size: 11px;
    font-weight: 600;
    border-radius: 10px;
    letter-spacing: 0.02em;
    border: 1px solid;
}
.probation-pill--yes {
    color: #047857;
    background: #ecfdf5;
    border-color: #d1fae5;
}
.probation-pill--no {
    color: #b91c1c;
    background: #fef2f2;
    border-color: #fecaca;
}
.group-header-label {
    /* Rendered inside the Type column of a divider row, but visually spans
       the whole row via CSS — the label pushes into overlapping columns.
       overflow: visible + a large min-width lets the text flow. */
    display: inline-block;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.02em;
    color: #4338ca;
    white-space: nowrap;
    text-transform: uppercase;
}
.group-subtotal-label {
    display: inline-block;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.02em;
    color: #b91c1c;
    white-space: nowrap;
    text-transform: uppercase;
}
</style>
